import { MESApiService, type spParameter } from "@dexteel/mesf-core";
import { get } from "lodash-es";
import { STAND_COLUMNS } from "@/pages/reports/billets-process-data/models/BilletsProcessData";
import type {
	BilletRFactorPickerBilletSize,
	BilletRFactorPickerCrew,
	BilletRFactorPickerProductSize,
	BilletRFactorPickerProductType,
	BilletRFactorPickers,
	BilletRFactorRow,
} from "../models/BilletsRFactor";

const round = (value: number | null | undefined, decimals: number) =>
	value == null ? null : Number.parseFloat(value.toFixed(decimals));

export interface GetBilletsRFactorParams {
	start: string;
	end: string;
	billetSizeId?: number | null;
	crewId?: string | null;
	productSizeId?: number | null;
	productTypeId?: number | null;
	signal?: AbortSignal;
}

export const getBilletsRFactor = async ({
	start,
	end,
	billetSizeId = null,
	crewId = null,
	productSizeId = null,
	productTypeId = null,
	signal,
}: GetBilletsRFactorParams): Promise<BilletRFactorRow[]> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "start", value: start },
		{ name: "end", value: end },
		{ name: "billetSizeId", value: billetSizeId },
		{ name: "crewId", value: crewId },
		{ name: "productSizeId", value: productSizeId },
		{ name: "productTypeId", value: productTypeId },
	];

	const resp = await apiService.callV2(
		"[MILL].[GetBilletsRFactorDataWeb]",
		parameters,
		signal,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching billets R-Factor");
	}

	const rows = get(resp, "data.tables[0].rows", []) as BilletRFactorRow[];
	return rows.map((row) => {
		const rounded: BilletRFactorRow = {
			...row,
			Length: round(row.Length, 2),
			BilletWeight: round(row.BilletWeight, 2),
		};
		for (const stand of STAND_COLUMNS) rounded[stand] = round(row[stand], 3);
		return rounded;
	});
};

export interface GetBilletsRFactorPickersParams {
	start: string;
	end: string;
	signal?: AbortSignal;
}

export const getBilletsRFactorPickers = async ({
	start,
	end,
	signal,
}: GetBilletsRFactorPickersParams): Promise<BilletRFactorPickers> => {
	const apiService = new MESApiService();
	const parameters: spParameter[] = [
		{ name: "start", value: start },
		{ name: "end", value: end },
		{ name: "onlyActives", value: true },
	];

	const resp = await apiService.callV2(
		"[MILL].[GetBilletsRFactorPickersDataWeb]",
		parameters,
		signal,
	);
	if (!resp.ok) {
		throw new Error(resp.message || "Error fetching billets R-Factor pickers");
	}

	// The first table names the ones after it, so a set is read by its name.
	const names = (
		get(resp, "data.tables[0].rows", []) as { Name: string }[]
	).map((meta) => meta.Name);
	const table = <T>(name: string): T[] => {
		const index = names.indexOf(name);
		return index < 0
			? []
			: (get(resp, `data.tables[${index + 1}].rows`, []) as T[]);
	};

	return {
		billetSizes: table<BilletRFactorPickerBilletSize>("BILLETSIZES"),
		crews: table<BilletRFactorPickerCrew>("CREWS").map((crew) => ({
			Id: crew.Id.trim(),
		})),
		productSizes: table<BilletRFactorPickerProductSize>("PRODUCTSIZES"),
		productTypes: table<BilletRFactorPickerProductType>("PRODUCTTYPES"),
	};
};
